import { useQuery } from '@tanstack/react-query';
import type { CourseSummary, SearchCoursesQuery } from '@admitly/shared';
import { apiGet } from '../lib/api.js';

export type CourseSearchResponse = {
  items: CourseSummary[];
  count: number;
  /**
   * Present (and ≥ 0) only when a fee band is active: how many otherwise-matching
   * courses are hidden because they have no annual fee recorded. Lets the screen
   * be honest that the fee filter is dropping fee-less courses.
   */
  hiddenNoFee?: number;
};

function toQueryString(q: SearchCoursesQuery): string {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.category) p.set('category', q.category.join(','));
  if (q.aqf) p.set('aqf', q.aqf.join(','));
  if (q.state) p.set('state', q.state.join(','));
  if (q.intake) p.set('intake', q.intake.join(','));
  if (q.ieltsMax !== undefined) p.set('ieltsMax', String(q.ieltsMax));
  if (q.feeMin !== undefined) p.set('feeMin', String(q.feeMin));
  if (q.feeMax !== undefined) p.set('feeMax', String(q.feeMax));
  if (q.pr) p.set('pr', 'true');
  if (q.anzsco) p.set('anzsco', q.anzsco);
  return p.toString();
}

export function useCourseSearch(query: SearchCoursesQuery) {
  const qs = toQueryString(query);
  return useQuery({
    queryKey: ['course-search', qs],
    queryFn: () => apiGet<CourseSearchResponse>(`/search/courses${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}
